import React from 'react';
import { Link } from 'react-router-dom';
import ImplantCenterLayout from './ImplantCenterLayout';
import ConsultationForm from '@/components/forms/ConsultationForm';
import InsuranceCarousel from '@/components/cards/InsuranceCarousel';
import GoogleReviewsWidget from '@/components/common/GoogleReviewsWidget';

const ImplantCenterHome = () => {
  const pageSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Dentist",
        "@id": "https://dentalimplants.fortworthdental.com/#dentist",
        "name": "Fort Worth Dental Implant Center",
        "description": "Premier dental implant center in Fort Worth TX offering affordable dental implants, same-day implants, mini implants, All-on-4, and financing options.",
        "url": "https://dentalimplants.fortworthdental.com",
        "telephone": "+1-817-854-4329",
        "priceRange": "$$",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "4620 Citylake Blvd W",
          "addressLocality": "Fort Worth",
          "addressRegion": "TX",
          "postalCode": "76132",
          "addressCountry": "US"
        },
        "geo": { "@type": "GeoCoordinates", "latitude": "32.6799", "longitude": "-97.4089" },
        "areaServed": ["Fort Worth", "Dallas", "Arlington", "Southlake", "Weatherford", "Stephenville", "Benbrook", "Aledo", "DFW Metroplex"],
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "500" }
      },
      {
        "@type": "MedicalProcedure",
        "name": "Dental Implant Surgery",
        "procedureType": "Surgical",
        "description": "Permanent tooth replacement using titanium implant posts fused to jawbone",
        "howPerformed": "Titanium post implanted into jawbone, followed by osseointegration and crown placement"
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How much do dental implants cost in Fort Worth?",
            "acceptedAnswer": { "@type": "Answer", "text": "Dental implant cost depends on the number of implants, bone grafting needs, and the type of restoration — every case is unique. Rather than publish prices that won't apply to your situation, we provide a personalized written quote at your FREE consultation. Flexible 0% financing plans available." }
          },
          {
            "@type": "Question",
            "name": "Are dental implants covered by insurance?",
            "acceptedAnswer": { "@type": "Answer", "text": "Many PPO dental insurance plans cover a portion of dental implant costs. We accept most major PPO plans and offer a $199/year dental plan for patients without insurance." }
          },
          {
            "@type": "Question",
            "name": "How long do dental implants last?",
            "acceptedAnswer": { "@type": "Answer", "text": "With proper care, dental implants can last a lifetime. The titanium posts fuse permanently with your jawbone through osseointegration, making them the most durable tooth replacement option available." }
          }
        ]
      }
    ]
  };

  const services = [
    {
      title: 'Dental Implants',
      description: 'Permanent tooth replacement solution using bio-compatible titanium posts that fuse with your jawbone.',
      link: '/implant-center/dental-implants',
      icon: '🦷'
    },
    {
      title: 'Mini Dental Implants',
      description: 'Smaller diameter implants ideal for patients with limited jawbone density. Less invasive, faster healing.',
      link: '/implant-center/mini-implants',
      icon: '📍'
    },
    {
      title: 'Jawbone Grafting',
      description: 'Fortify your jawbone to support dental implants. Includes sinus lift procedures when needed.',
      link: '/implant-center/jawbone-grafting',
      icon: '🦴'
    },
    {
      title: 'Cosmetic Dentistry',
      description: 'Complete smile makeovers including teeth whitening, veneers, Invisalign, and CEREC same-day crowns.',
      link: '/implant-center/cosmetic-dentistry',
      icon: '✨'
    }
  ];

  const benefits = [
    { title: '35+ Years Experience', description: 'Board-certified implant dentists with thousands of successful implants' },
    { title: 'FREE Consultation', description: 'Includes comprehensive exam and panoramic X-rays at no cost' },
    { title: 'Advanced 3D Imaging', description: '360-degree panoramic digital imaging for precise implant placement' },
    { title: 'Flexible Financing', description: '0% interest plans available through CareCredit and Lending Club' },
    { title: 'Same-Day Options', description: 'CEREC technology for same-day crowns and restorations' },
    { title: 'All-in-One Location', description: 'Complete implant care from consultation to final restoration' }
  ];

  return (
    <ImplantCenterLayout
      title="Affordable Dental Implants Fort Worth TX | FREE Consultation | Fort Worth Dental Implant Center"
      description="Fort Worth's premier dental implant center. Personalized treatment plans, FREE consultation with X-rays. Same-day implants, mini implants, All-on-4. Serving Fort Worth, Dallas, Arlington & DFW. Call (817) 854-4329."
      keywords="dental implants Fort Worth, affordable dental implants Fort Worth TX, dental implant cost Fort Worth, best dental implants near me, same day dental implants Fort Worth, mini dental implants Fort Worth, All-on-4 dental implants DFW, implant dentist Fort Worth Texas, dental implant financing Fort Worth"
      schema={pageSchema}
    >
      {/* Hero Section */}
      <section className="relative min-h-[500px] bg-gradient-to-br from-gray-900 to-gray-800">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40" 
          style={{backgroundImage: "url('/images/services/dentist-examines.jpg')"}}
        />
        <div className="relative container mx-auto px-4 py-20">
          <div className="max-w-3xl">
            <p className="text-teal-400 font-semibold text-lg mb-2">Fort Worth's Premier Dental Implant Center</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Affordable Dental Implants in Fort Worth, TX
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              Restore your smile with permanent dental implants. <strong>FREE consultation</strong> with X-rays. 
              Experienced implant dentists serving Fort Worth, Dallas, Arlington & the DFW metroplex for over 35 years.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="tel:8178544329" 
                className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-bold transition text-center"
              >
                Call (817) 854-4329
              </a>
              <a 
                href="#consultation-form" 
                className="bg-white hover:bg-gray-100 text-teal-700 px-8 py-4 rounded-lg text-lg font-bold transition text-center"
              >
                FREE Consultation
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="bg-teal-600 text-white py-6">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <p className="text-3xl font-bold">35+</p>
              <p className="text-sm">Years Experience</p>
            </div>
            <div>
              <p className="text-3xl font-bold">5,000+</p>
              <p className="text-sm">Implants Placed</p>
            </div>
            <div>
              <p className="text-3xl font-bold">4.9★</p>
              <p className="text-sm">Google Rating</p>
            </div>
            <div>
              <p className="text-3xl font-bold">0%</p>
              <p className="text-sm">Financing Available</p>
            </div>
          </div>
        </div>
      </section>

      {/* Welcome Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                Welcome to Fort Worth Dental's Implant Center
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Planning your <strong>dental implant placement in Fort Worth</strong> starts with a complete dental examination 
                to ensure you're a good candidate for dental implant surgery. Our advanced <strong>360-degree panoramic dental X-ray</strong> 
                technology thoroughly checks the condition of your teeth, gums, and jawbone.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                During the implant procedure, we administer sedation and numb the surgical area for your comfort. 
                To fully restore your smile, your <strong>dental implants</strong> must be composed of the best materials 
                and positioned optimally in your jaw for maximum durability and natural appearance.
              </p>
              <div className="bg-teal-50 border-l-4 border-teal-500 p-4 rounded">
                <p className="text-teal-800 font-semibold">Why Choose Us?</p>
                <p className="text-teal-700">Board-certified implant dentists, advanced 3D imaging technology, 
                and flexible financing options make us the top choice for dental implants in Fort Worth, TX.</p>
              </div>
            </div>
            <div>
              <img 
                src="/images/services/dental-implant-consultation.jpg" 
                alt="Dental implant consultation in Fort Worth TX" 
                className="rounded-xl shadow-xl w-full"
                onError={(e) => { e.target.src = '/images/services/dentist-examines.jpg'; }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Dental Implant Services in Fort Worth
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              We offer comprehensive dental implant solutions for patients throughout the DFW metroplex, 
              including Dallas, Arlington, Southlake, Weatherford, and Stephenville.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <Link 
                key={index} 
                to={service.link}
                className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition-shadow duration-300 group"
              >
                <div className="text-4xl mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-teal-600 transition">
                  {service.title}
                </h3>
                <p className="text-gray-600">{service.description}</p>
                <p className="text-teal-600 font-semibold mt-4 group-hover:underline">Learn More →</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Why Choose Fort Worth Dental Implant Center?
            </h2>
            <p className="text-gray-600 text-lg">
              The best dental implant experience in Fort Worth, TX
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-4 p-4">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 mb-1">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Consultation Form */}
      <section id="consultation-form" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Request Your FREE Dental Implant Consultation
              </h2>
              <p className="text-gray-600 text-lg">
                Includes comprehensive exam and panoramic X-rays. No obligation.
              </p>
            </div>
            <ConsultationForm />
          </div>
        </div>
      </section>

      {/* Google Reviews */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">What Our Patients Say</h2>
            <p className="text-gray-600">See why Fort Worth trusts us for dental implants</p>
          </div>
          <div className="max-w-xl mx-auto">
            <GoogleReviewsWidget variant="compact" />
          </div>
        </div>
      </section>

      {/* Insurance */}
      <InsuranceCarousel />

      {/* FAQ Section for SEO */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
              Frequently Asked Questions About Dental Implants
            </h2>
            
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  How much do dental implants cost in Fort Worth?
                </h3>
                <p className="text-gray-700">
                  Dental implant cost depends on the number of implants, bone grafting needs, and the type of restoration — every case is unique. We give you a precise, written quote at your free consultation rather than publishing one-size-fits-all numbers that may not apply to your situation. Flexible 0% financing is available.
                  Full mouth dental implants (All-on-4) — every case is unique. We offer flexible financing options 
                  including 0% interest plans through CareCredit and Lending Club.
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  Are dental implants covered by insurance?
                </h3>
                <p className="text-gray-700">
                  Many PPO dental insurance plans cover a portion of dental implant costs. We accept most major 
                  PPO plans including Delta Dental, Cigna, MetLife, Aetna, and Guardian. For patients without insurance, 
                  we offer a $199/year dental plan with 20% off all procedures.
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  How long do dental implants last?
                </h3>
                <p className="text-gray-700">
                  With proper care, dental implants can last a lifetime. The titanium posts fuse permanently with 
                  your jawbone through a process called osseointegration, making them the most durable tooth 
                  replacement option available. The crown may need replacement after 10-15 years.
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  Am I a good candidate for dental implants?
                </h3>
                <p className="text-gray-700">
                  Most people with missing teeth are good candidates for dental implants. Factors we evaluate include 
                  jawbone density, overall health, and oral hygiene habits. Even if you have bone loss, procedures 
                  like jawbone grafting or mini implants may make implants possible for you.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Local SEO Content */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Serving the Fort Worth & DFW Metroplex
            </h2>
            <p className="text-gray-600 mb-4">
              Fort Worth Dental Implant Center proudly serves patients seeking affordable dental implants throughout 
              the Dallas-Fort Worth metroplex. Our convenient location near I-20 and Bryant Irvin Road makes us 
              easily accessible from:
            </p>
            <p className="text-gray-500 text-sm">
              <strong>Fort Worth</strong> • <strong>Dallas</strong> • <strong>Arlington</strong> • 
              <strong>Southlake</strong> • <strong>Weatherford</strong> • <strong>Stephenville</strong> • 
              <strong>Benbrook</strong> • <strong>Aledo</strong> • <strong>Keller</strong> • 
              <strong>Grapevine</strong> • <strong>Burleson</strong> • <strong>Mansfield</strong>
            </p>
          </div>
        </div>
      </section>
    </ImplantCenterLayout>
  );
};

export default ImplantCenterHome;
